import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { getCookie, setCookie, removeCookie } from '@/shared/services/storage/cookieService';

const mutex = new Mutex();

interface RefreshTokenResponse {
  success: boolean;
  data: {
    access_token: string;
    refresh_token: string;
  };
}

interface ErrorData {
  code?: number;
  message?: string;
}

const customFetchBase = (baseUrl: string): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  return async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        const token = getCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME);
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
      }
    });

    // Retrieve user info from RTK Query cache
    await mutex.waitForUnlock();
    let result = await baseQuery(args, api, extraOptions);
    
    if (result?.error?.status === 401) {
      const errorData = result.error.data as ErrorData;
      if (errorData?.code === 40102) {
        if (!mutex.isLocked()) {
          const release = await mutex.acquire();
          try {
            const refreshToken = getCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME);
            const refreshResult = await baseQuery(
              {
                method: 'POST',
                url: '/api/mizfa_tools/refresh_token',
                body: { refresh_token: refreshToken }
              },
              api,
              extraOptions
            ) as { data?: RefreshTokenResponse; error?: FetchBaseQueryError };

            if (refreshResult?.data?.success) {
              setCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, refreshResult.data.data.access_token, 1);
              setCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, refreshResult.data.data.refresh_token, 7);
              result = await baseQuery(args, api, extraOptions);
            } else {
              console.log('REMOVE TOKEN');
              removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
              removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
              window.location.replace('/login');
            }
          } finally {
            release();
          }
        } else {
          // wait until the mutex is available without locking it
          await mutex.waitForUnlock();
          result = await baseQuery(args, api, extraOptions);
        }
      } else {
        removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
        removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
        window.location.replace('/login');
      }
    }
    return result;
  };
};

export default customFetchBase;

